import type { TLRichText, TLTextShape } from "@tldraw/tlschema";
type Node = {
  type: string;
  text?: string;
  content?: Node[];
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  [key: string]: unknown;
};

/** Explicit whole-box colours replace inline colour without dropping other styles. */
export function withoutTextColors(text: TLRichText): TLRichText {
  const visit = (node: Node): Node => ({
    ...node,
    ...(node.marks
      ? {
          marks: node.marks.flatMap((mark) => {
            if (mark.type !== "textStyle") return [mark];
            const { color: _color, ...attrs } = mark.attrs ?? {};
            return Object.keys(attrs).length ? [{ ...mark, attrs }] : [];
          }),
        }
      : {}),
    ...(node.content ? { content: node.content.map(visit) } : {}),
  });
  return visit(text as Node) as TLRichText;
}

export function textColorValues(text: TLRichText, fallback: string): string[] {
  const colors = new Set<string>();
  const visit = (node: Node) => {
    if (node.type === "text") {
      const color = node.marks?.find((mark) => mark.type === "textStyle")?.attrs
        ?.color;
      colors.add(typeof color === "string" && color ? color : fallback);
    }
    node.content?.forEach(visit);
  };
  visit(text as Node);
  return colors.size ? [...colors] : [fallback];
}
export function textHasMark(text: TLRichText, mark: string): boolean {
  const nodes: Node[] = [];
  const collect = (node: Node) => {
    if (node.type === "text") nodes.push(node);
    node.content?.forEach(collect);
  };
  collect(text as Node);
  return (
    nodes.length > 0 &&
    nodes.every((node) => node.marks?.some((m) => m.type === mark))
  );
}
export function toggleTextMark(text: TLRichText, mark: string): TLRichText {
  const remove = textHasMark(text, mark);
  const visit = (node: Node): Node => ({
    ...node,
    ...(node.type === "text"
      ? {
          marks: [
            ...(node.marks ?? []).filter((m) => m.type !== mark),
            ...(remove ? [] : [{ type: mark }]),
          ],
        }
      : {}),
    ...(node.content ? { content: node.content.map(visit) } : {}),
  });
  return visit(text as Node) as TLRichText;
}
export type TextListKind = "bulletList" | "orderedList";

export function toggleTextList(
  text: TLRichText,
  kind: TextListKind = "bulletList",
): TLRichText {
  const root = text as Node,
    content = root.content ?? [];
  if (content.length === 1 && content[0].type === kind)
    return {
      ...root,
      content: content[0].content?.flatMap((item) => item.content ?? []),
    } as TLRichText;
  const paragraphs = content.flatMap((node) =>
    node.type === "bulletList" || node.type === "orderedList"
      ? (node.content?.flatMap((item) => item.content ?? []) ?? [])
      : [node],
  );
  return {
    ...root,
    content: [
      {
        type: kind,
        content: paragraphs.map((node) => ({
          type: "listItem",
          content:
            node.type === "paragraph"
              ? [node]
              : [{ type: "paragraph", content: node.content ?? [] }],
        })),
      },
    ],
  } as TLRichText;
}

export function textIsList(
  text: TLRichText,
  kind: TextListKind = "bulletList",
): boolean {
  const content = (text as Node).content;
  return !!content?.length && content.every((node) => node.type === kind);
}

/** Remove character and paragraph styling while retaining words and breaks. */
export function clearTextFormatting(text: TLRichText): TLRichText {
  const visit = (node: Node): Node[] => {
    if (node.type === "bulletList" || node.type === "orderedList")
      return (node.content ?? []).flatMap((item) =>
        (item.content ?? []).flatMap(visit),
      );
    const content = node.content?.flatMap(visit);
    return [
      {
        ...node,
        ...(node.type === "heading" ? { type: "paragraph" } : {}),
        ...(node.type === "text" ? { marks: [] } : {}),
        ...(content ? { content } : {}),
        ...(node.type === "paragraph" || node.type === "heading"
          ? { attrs: {} }
          : {}),
      },
    ];
  };
  return visit(text as Node)[0] as TLRichText;
}

const SIZE_MULTIPLIER: Record<TLTextShape["props"]["size"], number> = {
  s: 1.125,
  m: 1.5,
  l: 2.25,
  xl: 2.75,
};

export function textFontSize(
  shape: TLTextShape,
  themeFontSize: number,
): number {
  const custom = Number(shape.meta.calqueTextSize);
  return Number.isFinite(custom) && custom >= 10 && custom <= 96
    ? custom
    : themeFontSize * SIZE_MULTIPLIER[shape.props.size];
}

export function textLineHeight(
  shape: TLTextShape,
  themeLineHeight: number,
): number {
  const custom = Number(shape.meta.calqueLineHeight);
  return Number.isFinite(custom) && custom >= 1 && custom <= 2.5
    ? custom
    : themeLineHeight;
}
export function resizedTextFrame(
  shape: Pick<TLTextShape, "x" | "y" | "rotation" | "props">,
  initialWidth: number,
  pointerX: number,
  left: boolean,
) {
  const width = Math.max(16, left ? initialWidth - pointerX : pointerX);
  const shift = left ? initialWidth - width : 0;
  return {
    x: shape.x + shift * Math.cos(shape.rotation),
    y: shape.y + shift * Math.sin(shape.rotation),
    props: { w: width / shape.props.scale, autoSize: false },
  };
}
